'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { Address } from 'viem'
import { getNFTMetadata, batchGetNFTMetadata, type NFTMetadata, MetadataError } from '@/util/nftMetadata'
import { type ChainType } from '@/constants/chain-specific'

interface UseNFTMetadataResult {
  metadata: NFTMetadata | null
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

interface UseNFTMetadataOptions {
  enabled?: boolean
  skipCollectionConfig?: boolean
  isERC1155?: boolean
  initialData?: NFTMetadata // For server-side hydration
}

/**
 * Hook for fetching single NFT metadata on client-side
 */
export function useNFTMetadata(
  contractAddress: Address | undefined,
  tokenId: bigint | undefined,
  chainId: ChainType | undefined,
  options: UseNFTMetadataOptions = {}
): UseNFTMetadataResult {
  const {
    enabled = true,
    skipCollectionConfig = false,
    isERC1155 = false,
    initialData
  } = options

  const [metadata, setMetadata] = useState<NFTMetadata | null>(initialData || null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchMetadata = useCallback(async () => {
    if (!contractAddress || tokenId === undefined || !chainId || !enabled) {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      console.log(`🎯 Client-side fetching metadata for ${contractAddress}:${tokenId.toString()}`)

      const result = await getNFTMetadata(contractAddress, tokenId, chainId, {
        useCache: true,
        skipCollectionConfig,
        isERC1155
      })

      setMetadata(result)
    } catch (err) {
      const errorMessage = err instanceof MetadataError
        ? `${err.code}: ${err.message}`
        : err instanceof Error
          ? err.message
          : String(err)

      console.error('Failed to fetch NFT metadata:', errorMessage)
      setError(errorMessage)

      // Set fallback metadata
      setMetadata({
        name: tokenId ? `Token #${tokenId.toString()}` : 'Unknown Token',
        description: 'Failed to load metadata',
        image: undefined,
        attributes: []
      })
    } finally {
      setIsLoading(false)
    }
  }, [contractAddress, tokenId, chainId, enabled, skipCollectionConfig, isERC1155])

  // Auto-fetch on mount and when dependencies change
  useEffect(() => {
    // Only fetch if we don't have initial data or if parameters changed
    if (enabled && contractAddress && tokenId !== undefined && chainId) {
      if (!initialData || metadata === null) {
        fetchMetadata()
      }
    }
  }, [contractAddress, tokenId, chainId, enabled, fetchMetadata, initialData, metadata])

  return {
    metadata,
    isLoading,
    error,
    refetch: fetchMetadata
  }
}

interface UseBatchNFTMetadataResult {
  metadataMap: Map<string, NFTMetadata>
  isLoading: boolean
  error: string | null
  loadingTokens: Set<string>
  refetch: () => Promise<void>
  refetchToken: (tokenId: bigint) => Promise<void>
}

interface UseBatchNFTMetadataOptions {
  enabled?: boolean
  skipCollectionConfig?: boolean
  isERC1155?: boolean
  batchSize?: number
  initialData?: Array<{ tokenId: bigint; metadata: NFTMetadata }> // For server-side hydration
}

/**
 * Hook for fetching multiple NFT metadata on client-side
 */
export function useBatchNFTMetadata(
  contractAddress: Address | undefined,
  tokenIds: bigint[] | undefined,
  chainId: ChainType | undefined,
  options: UseBatchNFTMetadataOptions = {}
): UseBatchNFTMetadataResult {
  const {
    enabled = true,
    skipCollectionConfig = false,
    isERC1155 = false,
    batchSize = 10,
    initialData = []
  } = options

  const [metadataMap, setMetadataMap] = useState<Map<string, NFTMetadata>>(() => {
    const map = new Map<string, NFTMetadata>()
    initialData.forEach(({ tokenId, metadata }) => {
      map.set(tokenId.toString(), metadata)
    })
    return map
  })

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadingTokens, setLoadingTokens] = useState<Set<string>>(new Set())
  const metadataMapRef = useRef(metadataMap)

  // Keep ref in sync with state
  useEffect(() => {
    metadataMapRef.current = metadataMap
  }, [metadataMap])

  // Keep track of in-flight requests to prevent duplicates
  const inFlightRequests = useRef(new Set<string>());

  const fetchBatchMetadata = useCallback(async () => {
    if (!contractAddress || !tokenIds || tokenIds.length === 0 || !chainId || !enabled) {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      console.log(`🎯 Client-side batch fetching metadata for ${tokenIds.length} tokens`)

      const requests = tokenIds.map(tokenId => ({
        contractAddress,
        tokenId,
        chainId,
        isERC1155
      }))

      const results = await batchGetNFTMetadata(requests, {
        useCache: true,
        batchSize,
        skipCollectionConfig
      })

      setMetadataMap(prevMap => {
        const newMap = new Map(prevMap)
        results.forEach(({ tokenId, metadata }) => {
          newMap.set(tokenId.toString(), metadata)
        })
        return newMap
      })

      // Check for errors
      const errors = results.filter(r => r.error)
      if (errors.length > 0) {
        console.warn(`Failed to fetch metadata for ${errors.length} tokens:`, errors)
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err)
      console.error('Failed to fetch batch NFT metadata:', errorMessage)
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [contractAddress, tokenIds, chainId, enabled, batchSize, skipCollectionConfig, isERC1155])

  const refetchToken = useCallback(async (tokenId: bigint) => {
    if (!contractAddress || !chainId || !enabled) {
      return
    }

    const tokenIdStr = tokenId.toString()
    setLoadingTokens(prev => new Set([...prev, tokenIdStr]))
    setError(null)

    try {
      console.log(`🎯 Client-side refetching metadata for token ${tokenIdStr}`)

      const metadata = await getNFTMetadata(contractAddress, tokenId, chainId, {
        useCache: false, // Force fresh fetch
        skipCollectionConfig,
        isERC1155
      })

      setMetadataMap(prevMap => {
        const newMap = new Map(prevMap)
        newMap.set(tokenIdStr, metadata)
        return newMap
      })

    } catch (err) {
      const errorMessage = err instanceof MetadataError
        ? `${err.code}: ${err.message}`
        : err instanceof Error
          ? err.message
          : String(err)

      console.error(`Failed to refetch metadata for token ${tokenIdStr}:`, errorMessage)
      setError(errorMessage)
    } finally {
      setLoadingTokens(prev => {
        const newSet = new Set(prev)
        newSet.delete(tokenIdStr)
        return newSet
      })
    }
  }, [contractAddress, chainId, enabled, skipCollectionConfig, isERC1155])

  // Auto-fetch on mount and when dependencies change
  useEffect(() => {
    if (enabled && contractAddress && tokenIds && tokenIds.length > 0 && chainId) {
      // Only fetch tokens we don't already have and aren't currently being fetched
      const missingTokens = tokenIds.filter(tokenId => {
        const tokenIdStr = tokenId.toString();
        return !metadataMapRef.current.has(tokenIdStr) && !inFlightRequests.current.has(tokenIdStr);
      });

      if (missingTokens.length > 0) {
        // Mark tokens as in-flight
        missingTokens.forEach(tokenId => {
          inFlightRequests.current.add(tokenId.toString());
        });

        fetchBatchMetadata().finally(() => {
          // Clear in-flight status after fetch completes
          missingTokens.forEach(tokenId => {
            inFlightRequests.current.delete(tokenId.toString());
          });
        });
      }
    }
  }, [contractAddress, tokenIds, chainId, enabled, fetchBatchMetadata]);

  return {
    metadataMap,
    isLoading,
    error,
    loadingTokens,
    refetch: fetchBatchMetadata,
    refetchToken
  }
}

/**
 * Hook for pre-loading metadata in the background
 */
export function usePreloadNFTMetadata() {
  const preloadMetadata = useCallback(async (
    contractAddress: Address,
    tokenId: bigint,
    chainId: ChainType,
    options: {
      skipCollectionConfig?: boolean
      isERC1155?: boolean
    } = {}
  ) => {
    try {
      // Fire and forget - just load into cache
      await getNFTMetadata(contractAddress, tokenId, chainId, {
        useCache: true,
        ...options
      })
      console.log(`🚀 Preloaded metadata for ${contractAddress}:${tokenId.toString()}`)
    } catch (error) {
      console.warn(`Failed to preload metadata for ${contractAddress}:${tokenId.toString()}:`, error)
    }
  }, [])

  const preloadBatchMetadata = useCallback(async (
    contractAddress: Address,
    tokenIds: bigint[],
    chainId: ChainType,
    options: {
      skipCollectionConfig?: boolean
      isERC1155?: boolean
      batchSize?: number
    } = {}
  ) => {
    try {
      const requests = tokenIds.map(tokenId => ({
        contractAddress,
        tokenId,
        chainId,
        isERC1155: options.isERC1155 || false
      }))

      // Fire and forget - just load into cache
      await batchGetNFTMetadata(requests, {
        useCache: true,
        batchSize: options.batchSize || 5,
        skipCollectionConfig: options.skipCollectionConfig || false
      })

      console.log(`🚀 Preloaded metadata for ${tokenIds.length} tokens`)
    } catch (error) {
      console.warn(`Failed to preload batch metadata:`, error)
    }
  }, [])

  return {
    preloadMetadata,
    preloadBatchMetadata
  }
}

// Query key factory for consistent cache management (for React Query compatibility)
export const nftMetadataKeys = {
  all: ['nftMetadata'] as const,
  single: (contractAddress: Address, tokenId: bigint, chainId: ChainType) =>
    [...nftMetadataKeys.all, contractAddress, tokenId.toString(), chainId] as const,
  batch: (contractAddress: Address, chainId: ChainType) =>
    [...nftMetadataKeys.all, 'batch', contractAddress, chainId] as const,
}
